/**
 * Popup Class
 */
export class Popup {

	/**
	 * Constructor
	 *
	 * @param {string} containerSelector
	 */
	/**
	 * Constructor accepts a single block element.
	 *
	 * @param {HTMLElement} block A single block element (container)
	 */
	constructor( block ) {

		this.block = block;
		this.triggers = [];
		this.activeTrigger = null;
		this.overlay = null;
		this.content = null;
		this.closeBtn = null;
		this.handlers = null;
		this.inited = false;

		if ( ! this.block ) {

			return;

		}

		this.init();

	}

	/**
	 * Returns configured selector for source elements that should be cloned into popup content.
	 * @return {string}
	 */
	getCopySelector() {

		if ( ! this.activeTrigger && ! this.block ) {

			return '';

		}

		return this.activeTrigger?.getAttribute( 'data-popup-copy-selector' ) ||
			this.block?.getAttribute( 'data-popup-copy-selector' ) ||
			'';

	}

	/**
	 * Returns selector for closest ancestor used as copy root.
	 * @return {string}
	 */
	getCopyRootClosestSelector() {

		if ( ! this.activeTrigger && ! this.block ) {

			return '';

		}

		return this.activeTrigger?.getAttribute( 'data-popup-copy-root-closest' ) ||
			this.block?.getAttribute( 'data-popup-copy-root-closest' ) ||
			'';

	}

	/**
	 * Returns popup content selector used as clone destination.
	 * @return {string}
	 */
	getContentSelector() {

		if ( ! this.activeTrigger && ! this.block ) {

			return '.js-popup-content';

		}

		return this.activeTrigger?.getAttribute( 'data-popup-content-selector' ) ||
			this.block?.getAttribute( 'data-popup-content-selector' ) ||
			'.js-popup-content';

	}

	/**
	 * Removes previously cloned popup nodes.
	 * @return {void}
	 */
	clearClonedContent() {

		const content = this.content || this.overlay?.querySelector( this.getContentSelector() );
		if ( ! content ) {

			return;

		}

		content.querySelectorAll( '[data-popup-cloned="true"]' ).forEach( ( node ) => {

			node.remove();

		} );

	}

	/**
	 * Clones selected source nodes into popup content.
	 * @return {void}
	 */
	populateContentFromSources() {

		const copySelector = this.getCopySelector();
		if ( ! copySelector ) {

			return;

		}

		const content = this.overlay?.querySelector( this.getContentSelector() );
		if ( ! content ) {

			return;

		}

		this.clearClonedContent();

		const closestSelector = this.getCopyRootClosestSelector();
		const copyRoot = closestSelector
			? ( this.activeTrigger?.closest( closestSelector ) || this.block )
			: this.block;

		copyRoot.querySelectorAll( copySelector ).forEach( ( sourceNode ) => {

			const clone = sourceNode.cloneNode( true );
			clone.setAttribute( 'data-popup-cloned', 'true' );
			if ( clone.hasAttribute( 'hidden' ) ) {

				clone.removeAttribute( 'hidden' );

			}
			if ( clone.id ) {

				clone.removeAttribute( 'id' );

			}
			clone.querySelectorAll( '[id]' ).forEach( ( node ) => node.removeAttribute( 'id' ) );
			content.appendChild( clone );

		} );

	}

	/**
	 * Prepare cloned videos for popup playback.
	 * @return {void}
	 */
	preparePopupVideos() {

		const content = this.content || this.overlay?.querySelector( this.getContentSelector() );
		if ( ! content ) {

			return;

		}

		content.querySelectorAll( 'video' ).forEach( ( video ) => {

			video.setAttribute( 'controls', '' );
			video.removeAttribute( 'loop' );

		} );

	}

	/**
	 * Synchronize aria-hidden state for popup overlay.
	 * @param {boolean} isOpen Whether popup is open.
	 * @return {void}
	 */
	setAriaHiddenState( isOpen ) {

		if ( ! this.overlay ) {

			return;

		}

		this.overlay.setAttribute( 'aria-hidden', isOpen ? 'false' : 'true' );

	}

	/**
	 * Tries to autoplay first popup video.
	 * @return {void}
	 */
	playFirstPopupVideo() {

		const content = this.content || this.overlay?.querySelector( this.getContentSelector() );
		const video = content?.querySelector( 'video' );
		if ( ! video ) {

			return;

		}

		const playPromise = video.play();
		if ( playPromise && typeof playPromise.catch === 'function' ) {

			playPromise.catch( () => {

				// Autoplay may be blocked by browser policy.

			} );

		}

	}

	/**
	 * Pause and reset videos currently rendered in popup content.
	 * @return {void}
	 */
	pausePopupVideos() {

		const content = this.content || this.overlay?.querySelector( this.getContentSelector() );
		if ( ! content ) {

			return;

		}

		content.querySelectorAll( 'video' ).forEach( ( video ) => {

			video.pause();
			try {

				video.currentTime = 0;

			} catch ( e ) {}

		} );

	}

	/**
	 * Destroy listeners for this instance.
	 * @return {void}
	 */
	destroy() {

		if ( ! this.handlers ) {

			return;

		}
		this.removeListenersForBlock();
		this.handlers = null;
		this.inited = false;

	}

	/**
	 * Internal: remove listeners for this instance using stored handler refs.
	 * @return {void}
	 */
	removeListenersForBlock() {

		const h = this.handlers;
		if ( !h ) {

			return;

		}
		if ( Array.isArray( h.triggers ) ) {

			h.triggers.forEach( ( trigger ) => {

				try {

					trigger.removeEventListener( 'click', h.onTriggerClick );

				} catch ( e ) {}

			} );

		}
		if ( h.closeBtn ) {

			try {

				h.closeBtn.removeEventListener( 'click', h.close );

			} catch ( e ) {}

		}
		try {

			h.overlay.removeEventListener( 'click', h.onOverlayClick );

		} catch ( e ) {}
		try {

			document.removeEventListener( 'keydown', h.onDocumentKeydown );

		} catch ( e ) {}

	}

	/**
	 * Init
	 */
	init() {

		if ( ! this.block ) {

			return;

		}
		const block = this.block;
		const triggers = [ ...block.querySelectorAll( '.js-popup-trigger' ) ];
		const overlay = block.querySelector( '.js-popup-overlay' );
		const content = overlay?.querySelector( '.js-popup-content' ) || null;
		const closeBtn = overlay?.querySelector( '.js-popup-close' ) || null;

		if ( triggers.length === 0 || ! overlay || ! closeBtn ) {

			return;

		}

		this.triggers = triggers;
		this.overlay = overlay;
		this.content = content;
		this.closeBtn = closeBtn;
		this.setAriaHiddenState( false );

		if ( this.inited ) {

			return;

		}

		/**
		 * Opens the popup overlay.
		 * @param {HTMLElement|null} trigger Trigger that opened popup.
		 * @return {void}
		 */
		const open = ( trigger ) => {

			this.activeTrigger = trigger || this.activeTrigger || this.triggers[ 0 ] || null;

			this.populateContentFromSources();
			this.preparePopupVideos();

			this.overlay?.classList.add( 'is-active' );
			document.documentElement.style.overflow = 'hidden';
			this.setAriaHiddenState( true );
			this.overlay?.setAttribute( 'role', 'dialog' );
			this.overlay?.setAttribute( 'aria-modal', 'true' );
			this.triggers.forEach( ( item ) => item.setAttribute( 'aria-expanded', item === this.activeTrigger ? 'true' : 'false' ) );
			this.overlay?.setAttribute( 'tabindex', '-1' );
			this.overlay?.focus();
			this.playFirstPopupVideo();

		};

		/**
		 * Closes the popup overlay.
		 * @return {void}
		 */
		const close = () => {

			this.overlay?.classList.remove( 'is-active' );
			this.pausePopupVideos();
			this.clearClonedContent();
			document.documentElement.style.overflow = '';
			this.setAriaHiddenState( false );
			this.triggers.forEach( ( item ) => item.setAttribute( 'aria-expanded', 'false' ) );
			this.overlay?.removeAttribute( 'tabindex' );
			if ( this.activeTrigger ) {

				try {

					this.activeTrigger.focus( { preventScroll: true } );

				} catch ( e ) {

					this.activeTrigger.focus();

				}

			}

		};

		/**
		 * Overlay click handler — closes when clicking backdrop.
		 * @param {Event} e
		 */
		const onOverlayClick = ( e ) => {

			if ( e.target === overlay ) {

				close();

			}

		};

		/**
		 * Document keydown handler — closes on Escape when overlay active.
		 * @param {KeyboardEvent} e
		 */
		const onDocumentKeydown = ( e ) => {

			if ( e.key === 'Escape' && overlay.classList.contains( 'is-active' ) ) {

				close();

			}

		};

		/**
		 * Trigger click handler.
		 * @param {Event} e
		 * @return {void}
		 */
		const onTriggerClick = ( e ) => {

			e.preventDefault();
			open( e.currentTarget );

		};

		this.triggers.forEach( ( trigger ) => trigger.addEventListener( 'click', onTriggerClick ) );
		if ( closeBtn ) {

			closeBtn.addEventListener( 'click', close );

		}
		overlay.addEventListener( 'click', onOverlayClick );
		document.addEventListener( 'keydown', onDocumentKeydown );

		this.handlers = {
			onTriggerClick,
			open,
			close,
			onOverlayClick,
			onDocumentKeydown,
			closeBtn,
			triggers: this.triggers,
			overlay,
		};
		this.inited = true;

	}

}
